/**
 * Duplicating a price matrix with adjustments — the arithmetic, alone.
 *
 * A copy is a matrix, not a snapshot: `{base[], rules[]}` in,
 * `{base[], rules[]}` out, so the result is live and drags its own
 * bindings exactly like the original (CU-869f9n466 phase 2).
 *
 * **One sentence decides everything here: an adjustment moves the
 * resulting floor of what it targets.** "Raise the premium group by
 * 3 %" raises the floor 3 %. Adding three percentage points to the band
 * instead — which is what the mock did — delivers 2.73 % on a 41 €
 * base, and that is Thibault's own case coming out wrong.
 *
 * Two kinds of adjustment, named rather than scoped. A selector reading
 * "all / some" would be a trap: with EDMR written as `+2 €` and CLMR as
 * `−2 %`, one "+5 % to all" moves EDMR 4.77 % and CLMR 5 %, for the
 * same words. So:
 *
 *   - `reference` — moves the reference price. Changes `base`, and every
 *     ACRISS follows through its own adjustment. A band written in
 *     €/day keeps its amount, so the gap between categories stays in
 *     euros: with +5 % on a 41 € base, an EDMR at `+2 €` goes 43,00 →
 *     45,05, not 45,15. Deliberate, and the preview shows it.
 *   - `cars` — moves those cars' floors. Changes their bands, exactly.
 *
 * Rounding happens **at every adjustment**, not once at the end: 1,00
 * with two +2,5 % is 1,06 step by step and 1,05 folded, and the user
 * sees the intermediate grid between one adjustment and the next. It is
 * also what keeps every step inside the two decimals the server's DTO
 * accepts (`schemas.py:128-129`).
 */

export const round2 = (n) => Math.round((Number(n) + Number.EPSILON) * 100) / 100

const covers = (band, lor) => (band.from == null || lor >= band.from)
                           && (band.to == null || lor <= band.to)

/** The base bands a rule band overlaps. More than one and no single
 *  converted value can be right across the whole band. */
function baseBandsUnder(base, band) {
    const from = band.from ?? 1
    const to = band.to
    return (base || []).filter(b => {
        const bTo = b.to == null ? Infinity : b.to
        const rTo = to == null ? Infinity : to
        return (b.from ?? 1) <= rTo && bTo >= from
    })
}

const bandLabel = (b) => (b.to == null ? `${b.from ?? 1}+` : `${b.from ?? 1}–${b.to}`)

const clone = (grid) => ({
    base: (grid.base || []).map(b => ({ ...b })),
    rules: (grid.rules || []).map(r => ({ ...r, bands: (r.bands || []).map(b => ({ ...b })) })),
})

/** Every ACRISS this matrix prices, reference first. */
export function codesOf(matrix) {
    return [matrix.base_acriss, ...(matrix.rules || []).map(r => r.acriss)].filter(Boolean)
}

/**
 * Apply `adjustments` in cascade to a matrix's grid.
 *
 * Returns `{grid, warnings, errors}`. `grid` is always a complete grid —
 * on an error it is whatever had been applied so far, so the preview can
 * still draw something; the caller refuses to save while `errors` is
 * non-empty.
 */
export function applyAdjustments(matrix, adjustments) {
    const grid = clone(matrix)
    const base_acriss = matrix.base_acriss
    const warnings = []
    const errors = []

    ;(adjustments || []).forEach((adj, i) => {
        const n = i + 1
        const amount = Number(adj.amount)
        if (adj.amount === '' || adj.amount == null || !Number.isFinite(amount) || amount < 0) {
            errors.push(`Adjustment ${n}: enter an amount of 0 or more — use the direction to go down.`)
            return
        }
        const s = (adj.sign === -1 ? -1 : 1) * amount
        const pct = adj.unit === 'percent'

        if (adj.kind === 'reference') {
            grid.base.forEach(b => {
                if (b.price == null) return
                b.price = round2(pct ? b.price * (1 + s / 100) : b.price + s)
            })
            return
        }

        const targets = (adj.acriss || []).filter(c => c && c !== base_acriss)
        if (!targets.length) {
            errors.push(`Adjustment ${n}: choose at least one ACRISS.`)
            return
        }
        targets.forEach(code => {
            const rule = grid.rules.find(r => r.acriss === code)
            if (!rule) return
            rule.bands.forEach(band => {
                if (band.value == null) return
                // Same unit: the floor moves by the adjustment with no
                // reference price involved.
                if ((band.unit === 'percent') === pct) {
                    band.value = round2(pct
                        ? ((1 + band.value / 100) * (1 + s / 100) - 1) * 100
                        : band.value + s)
                    return
                }
                // Mixed units: the band is rewritten in the adjustment's
                // unit, which needs the reference price of the band it
                // sits on — so the band has to sit on exactly one.
                const under = baseBandsUnder(grid.base, band)
                if (under.length !== 1) {
                    errors.push(`Adjustment ${n}: ${code} ${bandLabel(band)} spans ${under.length} `
                        + 'reference bands, so it cannot be rewritten in the other unit. '
                        + 'Re-cut the bands, or use the same unit as the band.')
                    return
                }
                const P = Number(under[0].price)
                if (!Number.isFinite(P) || P === 0) {
                    errors.push(`Adjustment ${n}: ${code} ${bandLabel(band)} sits on a reference price `
                        + 'of 0, so a percentage of it has no meaning.')
                    return
                }
                if (pct) {
                    // euro band, percentage adjustment → percent
                    band.value = round2(((P + band.value) * (1 + s / 100) - P) / P * 100)
                    band.unit = 'percent'
                } else {
                    // percent band, euro adjustment → euro
                    band.value = round2(P * band.value / 100 + s)
                    band.unit = 'euro'
                }
                warnings.push(`${code} ${bandLabel(band)}: the adjustment is in `
                    + `${pct ? '%' : '€/day'} and the band was not, so the band is rewritten in `
                    + `${pct ? '%' : '€/day'} on its reference price. A later adjustment in the `
                    + 'other unit will treat it as that.')
            })
        })
    })

    errors.push(...floorsBelowZero({ ...matrix, ...grid }))
    errors.push(...notFinite(grid))
    return { grid, warnings, errors }
}

/** Every value the server will reject as not a number. */
function notFinite(grid) {
    const out = []
    grid.base.forEach(b => {
        if (b.price != null && !Number.isFinite(Number(b.price))) out.push(`The reference price for ${bandLabel(b)} is not a number.`)
    })
    grid.rules.forEach(r => r.bands.forEach(b => {
        if (b.value != null && !Number.isFinite(Number(b.value))) out.push(`${r.acriss} ${bandLabel(b)} is not a number.`)
    }))
    return out
}

/** A floor of zero or less is not a floor. Checked on every cut, because
 *  a rule band can be negative only over part of the grid. */
function floorsBelowZero(matrix) {
    const out = []
    for (const row of cutsOf(matrix)) {
        for (const code of codesOf(matrix)) {
            const floor = floorOf(matrix, code, row.from)
            if (floor != null && floor <= 0) {
                out.push(`${code} ${bandLabel(row)} would end at ${floor.toFixed(2)} €. `
                    + 'A floor has to be above zero.')
            }
        }
    }
    return [...new Set(out)]
}

/**
 * The floor this grid sets for one ACRISS at one length of rental, or
 * `null` when it sets none. Same arithmetic as `priceMatrices.floorFor`
 * and as the engine's `floor_from_matrix`; kept here so this module is
 * pure and testable without importing the adapter's whole surface. The
 * test pins the two against each other.
 */
export function floorOf(matrix, acriss, lor) {
    const baseBand = (matrix.base || []).find(b => covers(b, lor))
    if (!baseBand || baseBand.price == null) return null
    if (acriss === matrix.base_acriss) return round2(baseBand.price)
    const rule = (matrix.rules || []).find(r => r.acriss === acriss)
    if (!rule) return null
    const b = (rule.bands || []).find(x => covers(x, lor))
    if (!b || b.value == null) return null
    return round2(b.unit === 'percent'
        ? baseBand.price * (1 + b.value / 100)
        : baseBand.price + b.value)
}

/**
 * The rows of the preview: the union of every cut in `base` and in every
 * rule's bands — not the base bands. A rule band that crosses a base cut
 * would otherwise be invisible, and those are exactly the ones worth
 * looking at.
 */
export function cutsOf(matrix) {
    const starts = new Set([1])
    const add = (b) => {
        if (b.from != null) starts.add(Number(b.from))
        if (b.to != null) starts.add(Number(b.to) + 1)
    }
    ;(matrix.base || []).forEach(add)
    ;(matrix.rules || []).forEach(r => (r.bands || []).forEach(add))
    const sorted = [...starts].filter(n => Number.isFinite(n) && n >= 1).sort((a, b) => a - b)
    return sorted.map((from, i) => ({
        from,
        to: i + 1 < sorted.length ? sorted[i + 1] - 1 : null,
    }))
}

/**
 * `[{ band, cells: { ACRISS: {before, after} } }]` — the whole preview,
 * computed on the grid as it will be saved, rounding included. A cell is
 * `null` where no band covers that length of rental, which the table
 * says as "no floor" because that is what the engine does.
 */
export function previewRows(before, after) {
    const codes = codesOf(before)
    return cutsOf({ ...before, rules: [...(before.rules || []), ...(after.rules || [])] })
        .map(band => ({
            band,
            cells: Object.fromEntries(codes.map(code => [code, {
                before: floorOf(before, code, band.from),
                after: floorOf({ ...after, base_acriss: before.base_acriss }, code, band.from),
            }])),
        }))
}

/** `"Winter (copy)"`, `"Winter (copy 2)"` … never longer than the 120
 *  characters the server accepts, never colliding with a name already in
 *  the list. The comparison is case-insensitive and trimmed because the
 *  server's is: MySQL compares on a `_ci` collation and `===` does not. */
export function copyName(name, existing, max = 120) {
    const taken = new Set((existing || []).map(n => String(n || '').trim().toLowerCase()))
    const base = String(name || 'Untitled').trim()
    for (let i = 1; i < 500; i++) {
        const suffix = i === 1 ? ' (copy)' : ` (copy ${i})`
        const room = max - suffix.length
        const candidate = (base.length > room ? base.slice(0, room).trim() : base) + suffix
        if (!taken.has(candidate.trim().toLowerCase())) return candidate
    }
    return `${Date.now()}`
}


/**
 * The exact copy both duplicate gestures produce (CU-869fe2gu2).
 *
 * The dialog's own composition — the matrix, then the grid of
 * `applyAdjustments` with no adjustments, then a name that clashes with
 * nothing in the list — and **no `id`**: the copy is a new row, and a pure
 * function that hands back another row's id is a trap even when the
 * caller overwrites it. Mutating the copy never touches the original
 * (`applyAdjustments` clones). "Exact" is enforced by test against the
 * dialog's formula and against the server payload's fingerprint.
 */
export function exactCopy(matrix, existingNames) {
    const { id, ...rest } = matrix || {}
    const { grid } = applyAdjustments(matrix, [])
    return { ...rest, ...grid, name: copyName(matrix?.name, existingNames) }
}
